import { z } from 'zod'

type ParsedLines<T> = {
  events: T[]
  /** The SSE `event:` name still in effect at the end of the processed lines (no blank line seen yet). */
  pendingEventName?: string
  /** The SSE `id:` of the event block still open at the end of the processed lines. */
  pendingEventId?: string
  /** The `id:` of the last event delivered, if it had one. */
  lastEventId?: string
}

function parseDataLines<T>(
  lines: string[],
  schema: z.ZodType<T>,
  allowedEventNames?: readonly string[],
  onDroppedEventName?: (eventName: string) => void
): ParsedLines<T> {
  const events: T[] = []
  let currentEventName: string | undefined
  let currentEventId: string | undefined
  let lastEventId: string | undefined

  for (const line of lines) {
    if (line.startsWith('event:')) {
      currentEventName = line.slice('event:'.length).trim()
      continue
    }

    if (line.startsWith('id:')) {
      currentEventId = line.slice('id:'.length).trim()
      continue
    }

    // A blank line terminates the current SSE event block and resets its name and id.
    if (line.trim() === '') {
      currentEventName = undefined
      currentEventId = undefined
      continue
    }

    if (!line.startsWith('data:')) continue

    // When an allowlist is provided, drop payloads of named events this stream does not consume
    // (e.g. periodic `keepalive` events) without attempting a parse. Whether a drop is worth reporting
    // depends on the stream — a keep-alive is expected, a `data-*` type from a newer producer is not — so
    // the decision belongs to the caller via `onDroppedEventName` rather than to this parser.
    if (allowedEventNames && currentEventName !== undefined && !allowedEventNames.includes(currentEventName)) {
      onDroppedEventName?.(currentEventName)
      continue
    }

    const jsonString = line.startsWith('data: ') ? line.slice(6) : line.slice(5)
    if (!jsonString) continue

    try {
      const rawEvent = JSON.parse(jsonString) as unknown
      const validatedEvent = schema.parse(rawEvent)
      events.push(validatedEvent)
      // Only a delivered event moves the id a reconnect resumes after, so it never skips one.
      lastEventId = currentEventId ?? lastEventId
    } catch (parseError) {
      console.error('Failed to parse SSE event:', parseError, 'Line:', line)
    }
  }

  return { events, pendingEventName: currentEventName, pendingEventId: currentEventId, lastEventId }
}

export function parseSSEChunk<T>(
  buffer: string,
  chunk: string,
  schema: z.ZodType<T>,
  allowedEventNames?: readonly string[],
  onDroppedEventName?: (eventName: string) => void
): { events: T[]; buffer: string; lastEventId: string | undefined } {
  const combined = buffer + chunk
  const lines = combined.split('\n')
  const remaining = lines.pop() ?? ''
  const { events, pendingEventName, pendingEventId, lastEventId } = parseDataLines(
    lines,
    schema,
    allowedEventNames,
    onDroppedEventName
  )
  // Carry an unterminated block's `event:` name and `id:` into the buffer so a `data:` line arriving
  // in the next chunk is still attributed to the right named event, and delivered with its id.
  const carried = [
    pendingEventName !== undefined ? `event: ${pendingEventName}\n` : '',
    pendingEventId !== undefined ? `id: ${pendingEventId}\n` : '',
  ].join('')
  return { events, buffer: `${carried}${remaining}`, lastEventId }
}
